// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The navigation glyphs (M3-37, 08 §3).
//
// Drawn here, in code, because every shipped asset in this product is (content rule: zero image
// files, zero audio files, zero models — it removes all asset licensing and the Store's
// third-party licence manifest along with it). An icon font would be a file; an SVG sprite would
// be a file; these are geometry.
//
// **Outline and filled are two different drawings, not one drawing with a different `fill`.** The
// fill is 08 §3's first of three cues for the active tab, and it has to survive being the *only*
// cue — a colour-blind player on a monochrome display should still see which tab they are on. A
// solid shape against a hollow one does that; a hollow shape tinted differently does not.

import type { NavIcon } from '../navigation'

export interface IconProps {
  readonly name: NavIcon
  readonly filled: boolean
  readonly size?: number
}

/**
 * One glyph, at one of two weights.
 *
 * `aria-hidden`, always. Every one of these sits beside a text label or inside a control that
 * carries its own accessible name, so a screen reader announcing the icon as well would read
 * every destination twice.
 */
export function Icon({ name, filled, size = 24 }: IconProps): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {GLYPHS[name](filled)}
    </svg>
  )
}

type Glyph = (filled: boolean) => React.JSX.Element

const paint = (filled: boolean): string => (filled ? 'currentColor' : 'none')

const GLYPHS: Readonly<Record<NavIcon, Glyph>> = {
  // A cabinet seen from the front: backbox, glass, and the two side rails.
  arcade: (filled) => (
    <>
      <rect x="5" y="2.5" width="14" height="6" rx="1.5" fill={paint(filled)} />
      <rect x="4" y="9.5" width="16" height="12" rx="2" fill={paint(filled)} />
      <path d="M8 21.5v-9M16 21.5v-9" stroke={filled ? 'none' : 'currentColor'} />
    </>
  ),
  // The product's own mark: a ball above two bats. The same shape as the favicon, so the tab and
  // the browser tab are recognisably one thing.
  play: (filled) => (
    <>
      <circle cx="12" cy="9" r="5" fill={paint(filled)} />
      <path d="M4 17.5 L9.5 20" />
      <path d="M20 17.5 L14.5 20" />
    </>
  ),
  // A podium: second, first, third. Reads as a leaderboard rather than as a generic chart.
  boards: (filled) => (
    <>
      <rect x="3" y="12" width="5.5" height="9" rx="1" fill={paint(filled)} />
      <rect x="9.25" y="7" width="5.5" height="14" rx="1" fill={paint(filled)} />
      <rect x="15.5" y="15" width="5.5" height="6" rx="1" fill={paint(filled)} />
    </>
  ),
  // A playfield with a part being added to it.
  build: (filled) => (
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" fill={paint(filled)} />
      <path d="M12 8.5v7M8.5 12h7" stroke={filled ? 'var(--accent-ink)' : 'currentColor'} strokeWidth={1.8} />
    </>
  ),
  you: (filled) => (
    <>
      <circle cx="12" cy="8" r="3.6" fill={paint(filled)} />
      <path d="M4.8 20.5a7.2 7.2 0 0 1 14.4 0" fill={paint(filled)} />
    </>
  ),
}
